interface Dimension {
  width: number;
  height: number;
}

/**
 * Resize a box to maxHeight and maxWidth and keep the intrinsic aspect ratio
 *
 * @param naturalWidth - intrinsic width of the box
 * @param naturalHeight - intrinsic height of the box
 * @param maxWidth - max new width width of the box
 * @param maxHeight - max new height height of the box
 * @param
 * @returns
 */
export const calculateAspectRatioFit = (
  naturalWidth: number,
  naturalHeight: number,
  maxWidth: number,
  maxHeight: number,
): Dimension => {
  const ratio = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight);
  const width = naturalWidth * ratio;
  const height = naturalHeight * ratio;

  return { width, height };
};

/**
 * Calculate height based on expected width.
 *
 * @param naturalWidth - widget width before recalculated to find aspect ratio
 * @param naturalHeight - widget height before recalculated to find aspect ratio
 * @param expectedWidth - new expected width
 * @returns { Dimension }
 */
export const calculateHeightByRatio = (
  naturalWidth: number,
  naturalHeight: number,
  expectedWidth: number,
): Dimension => {
  const ratio = naturalHeight / naturalWidth;
  const height = expectedWidth * ratio;
  return {
    width: expectedWidth,
    height,
  };
};

/**
 * Compute new object dimensions so that the object will fit within the container
 * The new icon will expand to fit either width or height of the widget and will be centered
 *
 * Optionally include a threshold to prevent the object from shrinking below a certain percentage of the container dimensions
 *
 * @param widgetDimension
 * @param viewBox
 * @returns
 */
const FIT_THRESHOLD = 0.85;
export const calculateDimensionFit = (
  containerDimension: { width: number; height: number },
  objectDimension: { width: number; height: number },
  threshold = FIT_THRESHOLD,
) => {
  const { width: containerWidth, height: containerHeight } = containerDimension;
  const { width: objectWidth, height: objectHeight } = objectDimension;

  const containerAspectRatio = containerWidth / containerHeight;
  const objectAspectRatio = objectWidth / objectHeight;

  const isContainerLandscape = containerWidth >= containerHeight;
  const isObjectLandscape = objectWidth >= objectHeight;
  const isSameOrientation = isContainerLandscape === isObjectLandscape;

  let newWidth = containerWidth;
  let newHeight = newWidth / objectAspectRatio;
  if (isSameOrientation) {
    // SVG and container have the same orientation
    if (containerAspectRatio > objectAspectRatio) {
      // Scale based on height
      newHeight = containerHeight;
      newWidth = newHeight * objectAspectRatio;
    } else {
      // Scale based on width
      newWidth = containerWidth;
      newHeight = newWidth / objectAspectRatio;
    }
  } else {
    // SVG and container have different orientations
    if (isContainerLandscape) {
      // SVG is portrait
      newHeight = containerHeight;
      newWidth = newHeight * objectAspectRatio;
    } else {
      // SVG is landscape
      newWidth = containerWidth;
      newHeight = newWidth / objectAspectRatio;
    }
  }

  // Based on the threshold, ensure the dimensions do not shrink below a certain percentage of the container dimensions
  const containerArea = containerWidth * containerHeight;
  const minArea = containerArea * threshold;
  if (newWidth * newHeight < minArea) {
    const scaleFactor = Math.sqrt(containerArea / (newWidth * newHeight));
    newWidth *= scaleFactor;
    newHeight *= scaleFactor;
  }

  let offsetX = (containerWidth - newWidth) / 2;
  let offsetY = (containerHeight - newHeight) / 2;

  return {
    widthPx: newWidth,
    heightPx: newHeight,
    offsetX,
    offsetY,
  };
};
